const PRINT_SERVICE_URLS = ["http://127.0.0.1:5000", "http://localhost:5000"];
const STATUS_TIMEOUT_MS = 800;
const CORS_CACHE_MS = 30000;
const LEGACY_CACHE_MS = 10 * 60 * 1000;

let detectedService = null;
let detectedAt = 0;
let detectionPromise = null;

const notifyStatus = (detail) => {
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("printer-status-change", { detail }));
};

const request = async (baseUrl, path, options = {}, timeoutMs = 8000) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(`${baseUrl}${path}`, {
      ...options,
      credentials: "omit",
      targetAddressSpace: "loopback",
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeout);
  }
};

const detectReadableService = async () => {
  const cacheMs = detectedService?.mode === "legacy" ? LEGACY_CACHE_MS : CORS_CACHE_MS;
  if (detectedService && Date.now() - detectedAt < cacheMs) return detectedService;
  if (detectionPromise) return detectionPromise;

  detectionPromise = (async () => {
    const probes = await Promise.all(PRINT_SERVICE_URLS.map(async (baseUrl) => {
      try {
        const response = await request(baseUrl, "/status", {}, STATUS_TIMEOUT_MS);
        const detail = await response.json();
        return { baseUrl, mode: "cors", detail, responseOk: response.ok };
      } catch (_) {
        return null;
      }
    }));
    // Ambas direcciones apuntan al mismo servicio local. La consulta paralela
    // evita acumular dos timeouts cuando el print antiguo no expone /status.
    detectedService = probes.find(Boolean) || { baseUrl: PRINT_SERVICE_URLS[0], mode: "legacy" };
    detectedAt = Date.now();
    return detectedService;
  })();

  try {
    return await detectionPromise;
  } finally {
    detectionPromise = null;
  }
};

export const getPrinterStatus = async () => {
  const service = await detectReadableService();
  if (service.mode === "cors") {
    const detail = service.detail || {};
    const result = {
      ...detail,
      online: service.responseOk && detail.online !== false && detail.connected !== false,
      protocol: "cors",
    };
    notifyStatus(result);
    return result;
  }

  const result = { online: null, protocol: "legacy", reason: "status-unreadable" };
  notifyStatus(result);
  return result;
};

const printDirect = async (baseUrl, data, readableResponse) => {
  const options = readableResponse ? {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  } : {
    method: "POST",
    mode: "no-cors",
    headers: { "Content-Type": "text/plain;charset=UTF-8" },
    body: JSON.stringify(data),
  };

  const response = await request(baseUrl, "/print", options);
  if (!readableResponse) {
    return { printed: true, submitted: true, online: true, protocol: "direct-legacy" };
  }

  const contentType = response.headers.get("content-type") || "";
  const detail = contentType.includes("application/json") ? await response.json() : null;
  if (!response.ok || detail?.online === false || detail?.connected === false || detail?.printed === false || detail?.success === false || detail?.error) {
    return {
      printed: false,
      online: detail?.online !== false && detail?.connected !== false,
      reason: response.status === 409 ? "duplicate" : "print-error",
      status: response.status,
      detail: detail?.message || detail?.msg || detail?.error,
    };
  }
  return { printed: true, online: true, protocol: "direct-cors", detail };
};

export const ToPrinter = async (data) => {
  try {
    // Enviar el ticket directamente; nunca esperar /status antes de imprimir.
    const baseUrl = detectedService?.baseUrl || PRINT_SERVICE_URLS[0];
    const result = await printDirect(baseUrl, data, detectedService?.mode !== "legacy");
    notifyStatus(result);
    return result;
  } catch (error) {
    const result = { printed: false, online: false, reason: error?.name === "AbortError" ? "timeout" : "service-unavailable", detail: error?.message };
    notifyStatus(result);
    return result;
  }
};

export const resetPrinterDetection = () => {
  detectedService = null;
  detectedAt = 0;
  detectionPromise = null;
};
